import { auditFieldsForCreate, currentUserId, newId } from '../engine/context'
import { runSystemAction } from '../engine/workflowEngine'
import { findRow, insertRow, readTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, idOf, roleCodesOf, statusCodeOf, workflowRunner } from './c07.shared'
import { slaValue } from './slaSetting'

/** Helper bersama C13 Stock Opname (sesi WF-OPN-01, selisih WF-DSR-01). */

export type Wh = 'RM' | 'FG'
export const ROLE = {
  admin: { RM: 'admin_wh_rm', FG: 'admin_wh_fg' },
  operator: { RM: 'operator_wh_rm', FG: 'operator_wh_fg' },
  supervisor: { RM: 'wh_rm_supervisor', FG: 'supervisor_wh_fg' },
  kadiv: { RM: 'kadiv_wh_rm', FG: 'kadiv_wh_fg' }
} as const
export const OWNER_LABEL: Record<Wh, string> = { RM: 'Admin WH RM', FG: 'Admin WH FG' }
export const SUPERVISOR_LABEL: Record<Wh, string> = { RM: 'WH RM Supervisor', FG: 'Supervisor WH FG' }
export const KADIV_LABEL: Record<Wh, string> = { RM: 'Kadiv WH RM', FG: 'Kadiv WH FG' }
/**
 * Bawaan seed window investigasi (BA 12F OPN-04): [hari eskalasi, hari target approval]. HANYA untuk seed — handler memakai
 * `dscWindow(wh)` (Master SLA `DSC_ESCALATE_*` / `DSC_APPROVAL_*`).
 */
export const WINDOW: Record<Wh, [number, number]> = { RM: [5, 7], FG: [0, 1] }
/** Target akurasi Matched (%) — Master SLA `OPNAME_MATCHED_TARGET`. */
export const matchedTarget = () => slaValue('OPNAME_MATCHED_TARGET')

export const opnFlow = workflowRunner('WF-OPN-01', 'opname_session')
export const dsrFlow = workflowRunner('WF-DSR-01', 'discrepancy')

export const num = (value: unknown) => Number(value ?? 0)
export const round3 = (value: number) => Math.round(value * 1000) / 1000
export const iso = () => new Date().toISOString()
/** `YYYY-MM-DD HH:mm` WIB. */
export const wibStamp = (value: string | number | Date = Date.now()) =>
  new Date(new Date(value).getTime() + 7 * 3600_000).toISOString().slice(0, 16).replace('T', ' ')
export const wibDate = (value: string | number | Date = Date.now()) => wibStamp(value).slice(0, 10)
export const up = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()

export const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}
export const firstName = (id: unknown) => String(findRow('directus_users', idOf(id))?.first_name ?? '')

export const warehouseId = (wh: Wh) => String(readTable('mt_warehouses').find(row => row.code === `WH-${wh}`)?.id ?? '')
export const whOf = (warehouse: unknown): Wh =>
  findRow('mt_warehouses', idOf(warehouse))?.code === 'WH-FG' ? 'FG' : 'RM'
export const parseWh = (value: unknown): Wh =>
  String(value ?? '')
    .toUpperCase()
    .includes('FG')
    ? 'FG'
    : 'RM'

export const myRoles = () => roleCodesOf(currentUserId())
export const hasRole = (wh: Wh, kind: keyof typeof ROLE) => myRoles().includes(ROLE[kind][wh])
export function requireWhRole(wh: Wh, kind: keyof typeof ROLE, message: string) {
  if (!hasRole(wh, kind)) fail(message, 'ERR_UNAUTHORIZED_ROLE', 403)
}
/** Gudang yang dilihat persona aktif (FG bila peran FG, selain itu RM). */
export const defaultWh = (): Wh => (myRoles().some(code => code.endsWith('_fg')) ? 'FG' : 'RM')

export const operatorsOf = (wh: Wh) =>
  readTable('mt_privileges')
    .filter(row => String(findRow('mt_roles', idOf(row.role))?.code ?? '') === ROLE.operator[wh])
    .map(row => idOf(row.user))
    .map(id => ({ id, label: userName(id) }))
    .sort((a, b) => a.label.localeCompare(b.label))

export type OpnLog = { at: string; ev: string; by: string | null }
export const logOf = (row: LocalRow | undefined) => ((row?.log as OpnLog[] | undefined) ?? []).slice()
export const withLog = (row: LocalRow, ev: string, by: string | null = currentUserId()) => [
  ...logOf(row),
  { at: wibStamp(), ev, by }
]

export function txLogWh(wh: Wh, pageCode: string, action: string, docRef: string, detail: string) {
  insertRow('tx_log', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: iso(),
    log_type: 'Stock Opname',
    page_code: pageCode,
    action,
    doc_ref: docRef,
    detail,
    warehouse_id: warehouseId(wh),
    user_id: currentUserId()
  })
}

export function movement(
  type: string,
  ref: { unit_id?: unknown; label_id?: unknown },
  qtySmall: number,
  docRef: string,
  extra: Record<string, unknown> = {}
) {
  insertRow('stock_movement', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: iso(),
    movement_type: type,
    unit_id: ref.unit_id ?? null,
    label_id: ref.label_id ?? null,
    qty_small: qtySmall,
    from_rack_bin_id: null,
    to_rack_bin_id: null,
    from_warehouse_zone_id: null,
    to_warehouse_zone_id: null,
    from_pallet_id: null,
    to_pallet_id: null,
    from_container_id: null,
    to_container_id: null,
    doc_ref: docRef,
    user_id: currentUserId(),
    ...extra
  })
}

/** Akhir hari ke-`days` sesudah `at` (WIB) — window H+n. */
export const dueEndOfDay = (at: string | number, days: number) => {
  const wib = new Date(new Date(at).getTime() + 7 * 3600_000)
  const end = Date.UTC(wib.getUTCFullYear(), wib.getUTCMonth(), wib.getUTCDate() + days, 23, 59, 59)
  return new Date(end - 7 * 3600_000).toISOString()
}
/** Selisih hari kalender WIB (b − a). */
export const dayDiff = (a: string | number, b: string | number = Date.now()) =>
  Math.round((Date.parse(`${wibDate(b)}T00:00:00Z`) - Date.parse(`${wibDate(a)}T00:00:00Z`)) / 86_400_000)

/** Transisi tanpa actor manusia (pengganti worker SLA) — alias `runSystemAction` (engine): sekaligus memicu notifikasi. */
export function systemTransition(workflowCode: string, row: LocalRow, actionName: string, reason: string) {
  runSystemAction(workflowCode, row, actionName, reason)
}

/* ============================================================ master ringkas */

export const materialName = (id: unknown) => String(findRow('mt_material', idOf(id))?.name ?? '')
export const materialCode = (id: unknown) => String(findRow('mt_material', idOf(id))?.code ?? '')
export const skuName = (id: unknown) => String(findRow('mt_product', idOf(id))?.name ?? '')
export const skuCode = (id: unknown) => String(findRow('mt_product', idOf(id))?.code ?? '')
export const binCode = (id: unknown) => String(findRow('mt_rack_bin', idOf(id))?.code ?? '')
export const binByCode = (code: string) => readTable('mt_rack_bin').find(row => row.code === code)
export const reasonName = (id: unknown) => String(findRow('mt_reject_type', idOf(id))?.name ?? '')
export const perBigOf = (unit: LocalRow | undefined) =>
  Math.max(
    1,
    num(unit?.small_per_big_snapshot) ||
      num(findRow('mt_material', idOf(unit?.material_id))?.default_purchase_unit_qty) ||
      1
  )
export const bigQty = (unit: LocalRow) => round3(num(unit.qty_small) / perBigOf(unit))
export const unitNameOf = (unit: LocalRow) =>
  String(
    unit.unit_big_snapshot ??
      findRow('mt_unit', idOf(findRow('mt_material', idOf(unit.material_id))?.default_purchase_unit_id))?.name ??
      ''
  )
export const palletOf = (id: unknown) => findRow('pallet', idOf(id))
export const containerOf = (id: unknown) => findRow('container', idOf(id))
/** Lokasi FG sebuah label: kode pallet atau container. */
export const labelLocation = (label: LocalRow) =>
  String(palletOf(label.pallet_id)?.code ?? containerOf(label.container_id)?.code ?? '')
export const batchNoOf = (id: unknown) => String(findRow('fg_labeler_batch', idOf(id))?.batch_no ?? '')

export const sessionOf = (id: unknown) => {
  const row = findRow('opname_session', idOf(id))
  if (!row) fail('Sesi opname tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row as LocalRow
}
export const taskOf = (id: unknown) => {
  const row = findRow('opname_task', idOf(id))
  if (!row) fail('Tugas opname tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row as LocalRow
}
export const discOf = (id: unknown) => {
  const row = findRow('discrepancy', idOf(id))
  if (!row) fail('Discrepancy tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row as LocalRow
}
export const tasksOf = (sessionId: unknown) =>
  readTable('opname_task')
    .filter(task => idOf(task.session_id) === idOf(sessionId))
    .sort((a, b) => String(a.opt_no).localeCompare(String(b.opt_no)))
export const scansOf = (taskId: unknown) => readTable('opname_scan').filter(scan => idOf(scan.task_id) === idOf(taskId))
export const discsOfSession = (sessionId: unknown) =>
  readTable('discrepancy').filter(disc => idOf(disc.session_id) === idOf(sessionId) && !disc.parent_id)

export { statusCodeOf }
export const TERMINAL_DSR = ['DSR_RESOLVED_FOUND', 'DSR_ADJUSTED_WRITE_OFF']
export const OPEN_SESSION = ['OPN_SCHEDULED', 'OPN_OPEN', 'OPN_COUNTING', 'OPN_VALIDATION']
